/**
 * Sky slice build (ADR-0031): Godot-space sky properties, directional lights and
 * a panorama texture to the sky shader's THREE uniforms. Like the engine, this
 * converts on upload: easing curves to "ad hoc" reciprocals, `sun_angle_max` to
 * its cosine, each colour multiplied by its energy before its linear conversion.
 */
import * as THREE from 'three';
import { materialReadsLights } from './readsLights';
import { scaledRgb } from '../../utils/colorParser';
import { godotColorToLinear } from '../../r3f/godotColor';
import { degToRad } from '../../godot/math/math';
/** Godot's sky shader has four light slots, written out longhand. */
const LIGHT_SLOTS = 4;
/**
 * Godot scales the sRGB colour by its energy, then converts the `source_color` uniform to linear
 * (`sky_material.cpp:41`, `material_storage.cpp:459`), so an energy above 1 brightens by more
 * than its factor.
 */
function scaledColor(color, energy) {
    return godotColorToLinear(scaledRgb(color, energy));
}
export function skyUniforms(sky, lights, panorama) {
    const uniforms = {};
    switch (sky.kind) {
        case 'procedural':
            Object.assign(uniforms, {
                sky_top_color: { value: scaledColor(sky.sky_top_color, sky.sky_energy_multiplier) },
                sky_horizon_color: {
                    value: scaledColor(sky.sky_horizon_color, sky.sky_energy_multiplier),
                },
                inv_sky_curve: { value: 0.6 / sky.sky_curve },
                ground_bottom_color: {
                    value: scaledColor(sky.ground_bottom_color, sky.ground_energy_multiplier),
                },
                ground_horizon_color: {
                    value: scaledColor(sky.ground_horizon_color, sky.ground_energy_multiplier),
                },
                inv_ground_curve: { value: 0.6 / sky.ground_curve },
                sun_angle_max: { value: Math.cos(degToRad(sky.sun_angle_max)) },
                inv_sun_curve: { value: 1.6 / Math.pow(sky.sun_curve, 1.4) },
                exposure: { value: sky.energy_multiplier },
            });
            break;
        case 'panorama':
            Object.assign(uniforms, {
                source_panorama: { value: panorama ?? null },
                has_panorama: { value: !!panorama },
                exposure: { value: sky.energy_multiplier },
            });
            break;
        case 'physical':
            Object.assign(uniforms, {
                rayleigh: { value: sky.rayleigh_coefficient },
                rayleigh_color: { value: godotColorToLinear(sky.rayleigh_color) },
                mie: { value: sky.mie_coefficient },
                mie_eccentricity: { value: sky.mie_eccentricity },
                mie_color: { value: godotColorToLinear(sky.mie_color) },
                turbidity: { value: sky.turbidity },
                sun_disk_scale: { value: sky.sun_disk_scale },
                ground_color: { value: godotColorToLinear(sky.ground_color) },
                exposure: { value: sky.energy_multiplier },
            });
            break;
    }
    // A sky that reads no lights has no light slots, which would declare uniforms its GLSL never reads.
    if (materialReadsLights(sky)) {
        for (let i = 0; i < LIGHT_SLOTS; i++) {
            const light = lights[i];
            uniforms[`LIGHT${i}_ENABLED`] = { value: !!light };
            uniforms[`LIGHT${i}_DIRECTION`] = {
                value: light ? light.direction.clone().normalize() : new THREE.Vector3(0, 1, 0),
            };
            uniforms[`LIGHT${i}_COLOR`] = { value: light ? light.color.clone() : new THREE.Color(1, 1, 1) };
            uniforms[`LIGHT${i}_ENERGY`] = { value: light?.energy ?? 0 };
            uniforms[`LIGHT${i}_SIZE`] = { value: light?.size ?? 0 };
        }
    }
    return uniforms;
}
//# sourceMappingURL=skyUniforms.js.map